import PropTypes from 'prop-types'
import React from 'react'
import { Tooltip } from 'react-wot-bootstrap'

import {
    SORTIES_START_INTERIM,
    FORT_BATTLES_START_INTERIM,
    UNIT_TYPES,
} from 'constants'
import {
    BATTLE_WIZARD_REWARD_TOOLTIP,
    BATTLE_WIZARD_REWARD_TOOLTIP_X2,
    STATIC_TOOLTIPS_ID,
    STATIC_TOOLTIPS_MESSAGES,
} from './constants.jsx'

import ArenaTooltip from 'components/ArenaTooltip/ArenaTooltip.jsx'
import LoadingScreen from 'components/LoadingScreen/LoadingScreen.jsx'
import IndustrialResourceCoefTooltip from 'components/IndustrialResourceCoefTooltip/IndustrialResourceCoefTooltip'

import {
    Arenas,
    Battle,
    BattleTitleTooltip,
    Direction,
    FortBattleTooltip,
    Level,
    Options,
    Reward,
    SortieTooltip,
    UnavailableBattle,
    WizardButton,
    WizardDefaultTooltip,
} from './components'

import { getPeripheryById } from 'helpers/peripheries'
import { INDUSTRIAL_RESOURCE_MULTIPLIER, INDUSTRIAL_RESOURCE_MULTIPLIER_TEXT } from 'helpers/i18n'

import styles from './BattleWizard.scss'

export default class BattleWizard extends React.Component {
    static propTypes = {
        clanIndustrialResource: PropTypes.number,
        loaded: PropTypes.bool.isRequired,
        loading: PropTypes.bool.isRequired,
        fortBattles: PropTypes.object.isRequired,
        sorties: PropTypes.object.isRequired,
        peripheryId: PropTypes.number.isRequired,
        unitInfo: PropTypes.object,
        isRebuild: PropTypes.bool,
        showSchedule: PropTypes.bool,
        industrialResourceMultiplier: PropTypes.number.isRequired,

        submit: PropTypes.func.isRequired,
        closeWindow: PropTypes.func,
    }
    constructor(props) {
        super(props)

        this.update(props)
    }

    componentWillReceiveProps(nextProps) {
        this.update(nextProps)
    }

    componentWillUnmount() {
        this.clearTimeouts()
    }

    update(props = this.props) {
        if (!props.loaded || props.loading) return

        const nextUpdateAt = props.expiresAt
        if (nextUpdateAt <= Date.now()) return

        const timeout = nextUpdateAt - Date.now()

        this.clearTimeouts()
        this.timeoutId = setTimeout(props.requestInfo, timeout)
    }

    clearTimeouts() {
        if (this.timeoutId) clearTimeout(this.timeoutId)
    }

    getSortiesContent() {
        const {
            battle_modes: battleModes,
            peripheries,
            primetimeStart,
            primetimeEnd,
        } = this.props.sorties

        const periphery = getPeripheryById(peripheries, this.props.peripheryId)

        const peripheryPrimetimeStart = periphery && periphery.primetime_start

        const peripheryName = (periphery !== undefined) ? periphery.name : null

        const msecondsToStart = primetimeStart ? (primetimeStart - Date.now()) : null
        const msecondsToEnd = primetimeEnd ? (primetimeEnd - Date.now()) : null

        const primetimeInProgress = (
            (msecondsToStart === null) &&
            (msecondsToEnd !== null) &&
            (msecondsToEnd < Date.now())
        )
        const primetimeStartsSoon = (
            (msecondsToStart !== null) &&
            (msecondsToStart <= SORTIES_START_INTERIM)
        )
        const isAvailable = battleModes.length > 0 && (primetimeInProgress || primetimeStartsSoon)

        if (!isAvailable) return {
            content: (
                <UnavailableBattle
                    type={UNIT_TYPES.SORTIE}
                    peripheries={peripheries}
                    peripheryName={peripheryName}
                    primetimeStart={peripheryPrimetimeStart}
                />
            ),
            tooltips: null,
        }

        const getTooltipId = (division) => `sortie-division-${division}-tooltip`
        const getIsSortieSelected = (division) => {
            if (!this.props.unitInfo) return false
            return this.props.unitInfo.type === 'SORTIE' &&
                this.props.unitInfo.battle_mode === division
        }
        const tooltips = battleModes.map(({ division }) => (
            <SortieTooltip
                key={getTooltipId(division)}
                tooltipId={getTooltipId(division)}
                division={division}
                isSelected={getIsSortieSelected(division)}
                isRebuild={this.props.isRebuild}
            />
        ))

        const buttons = battleModes.map(item => {
            const handleClick = getIsSortieSelected(item.division) ?
                this.props.closeWindow :
                this.props.submit.bind(null, 'SORTIE', item.division, null)
            return (
                <WizardButton
                    key={`sorties-button-${item.type}`}
                    handleClick={handleClick}
                    // WGSH-2679 pass max vehicle level instead of division. Modify level component
                    iconComponent={<Level level={item.division} tooltipId={getTooltipId(item.division)} />}
                    contentComponent={<Options battleRegulations={item} />}
                    rewardComponent={<Reward value={item.reward} valueTooltipId={STATIC_TOOLTIPS_ID.sortie_reward} />}
                    isSelected={getIsSortieSelected(item.division)}
                />
            )
        })

        return {
            content: (
                <Battle
                    type={UNIT_TYPES.SORTIE}
                    primetimeStart={primetimeStart}
                    primetimeEnd={primetimeEnd}
                >
                    {buttons}
                </Battle>
            ),
            tooltips,
        }
    }

    getFortBattlesContent() {
        const {
            directions,
            seconds_to_next_match_maker: secondsToNextMatchMaker,
            peripheries,
            battle_mode: battleMode,
        } = this.props.fortBattles

        const msecondsToMatchMaker = (secondsToNextMatchMaker ? secondsToNextMatchMaker * 1000 : null)
        const isAvailable = (
            (msecondsToMatchMaker !== null) &&
            (msecondsToMatchMaker > 0) &&
            (msecondsToMatchMaker <= FORT_BATTLES_START_INTERIM)
        )
        const periphery = getPeripheryById(peripheries, this.props.peripheryId)

        const peripherySchedule = periphery ? periphery.matchmaker_ticks : []

        const peripheryName = periphery ? periphery.name : null

        const isFine = this.props.clanIndustrialResource < 0
        if (!isAvailable || isFine) {
            const primetimeStart = peripherySchedule.find(tick => tick > Date.now())

            return {
                content: (
                    <UnavailableBattle
                        type={UNIT_TYPES.FORT_BATTLE}
                        peripheries={peripheries}
                        peripheryName={peripheryName}
                        primetimeStart={primetimeStart || null}
                        isFine={isFine}
                    />
                ),
                tooltips: null,
            }
        }

        const getButtonTooltipId = (direction) => `fort-battle-direction-${direction}-tooltip`
        const getRewardTooltipId = (direction) => `fort-battle-direction-${direction}-reward-tooltip`
        const getIsFortBattleSelected = (direction) => {
            if (!this.props.unitInfo) return false
            return this.props.unitInfo.type === 'FORT_BATTLE' &&
                this.props.unitInfo.direction === direction
        }

        let buttons
        let tooltips
        if (directions !== null) {
            buttons = directions.map(item => {
                const isSelected = getIsFortBattleSelected(item.direction)
                const iconComponent = (
                    <Direction
                        key={`icon-${item.direction}`}
                        direction={item.direction}
                        bonus={item.is_first_fort_battle_on_direction}
                        battle={item.is_engaged && !isSelected}
                        tooltipId={item.is_engaged ? null : getButtonTooltipId(item.direction)}
                        isSelected={isSelected}
                    />
                )
                const rewardComponent = (
                    <Reward
                        value={item.max_reward}
                        perBattleReward={battleMode.reward}
                        valueTooltipId={item.is_engaged ? null : getRewardTooltipId(item.direction)}
                        perBattleTooltipId={item.is_engaged ? null : STATIC_TOOLTIPS_ID.per_battle}
                        bonus={item.is_first_fort_battle_on_direction}
                    />
                )
                let handleClick = this.props.submit.bind(
                    null,
                    'FORT_BATTLE',
                    null,
                    item.direction
                )
                if (isSelected) {
                    handleClick = this.props.closeWindow
                } else if (item.is_engaged) {
                    handleClick = null
                }

                return (
                    <WizardButton
                        key={`fort-battle-button-${item.direction}`}
                        handleClick={handleClick}
                        iconComponent={iconComponent}
                        contentComponent={<Arenas arenas={item.arenas} direction={item.direction} />}
                        rewardComponent={rewardComponent}
                        bonus={item.is_first_fort_battle_on_direction}
                        battle={item.is_engaged && !isSelected}
                        isSelected={isSelected}
                        tooltipId={item.is_engaged ? getButtonTooltipId(item.direction) : null}
                    />
                )
            })

            tooltips = directions.reduce((initial, item) => {
                const {
                    arenas,
                    direction,
                } = item

                const arenaTooltips = arenas.map((arena, idx) => {
                    const tooltipId = `arena-${direction}-${idx}-tooltip`
                    return (
                        <ArenaTooltip
                            key={tooltipId}
                            tooltipId={tooltipId}
                            point={idx}
                            direction={direction}
                            arena={arena}
                        />
                    )
                })
                const buttonTooltip = (
                    <FortBattleTooltip
                        tooltipId={getButtonTooltipId(direction)}
                        key={getButtonTooltipId(direction)}
                        direction={direction}
                        bonus={item.is_first_fort_battle_on_direction}
                        battle={item.is_engaged}
                        isSelected={getIsFortBattleSelected(direction)}
                        isRebuild={this.props.isRebuild}
                    />
                )
                const tooltipText = (
                    item.is_first_fort_battle_on_direction ?
                        BATTLE_WIZARD_REWARD_TOOLTIP_X2 :
                        BATTLE_WIZARD_REWARD_TOOLTIP
                )
                const rewardValueTooltip = (
                    <Tooltip
                        isWide
                        id={getRewardTooltipId(direction)}
                        key={getRewardTooltipId(direction)}
                    >
                        <div className={styles.tooltipContent}>
                            <p className={styles.tooltipMessage}>{tooltipText}</p>
                        </div>
                    </Tooltip>
                )

                const tooltipsSet = item.is_engaged ? [
                    buttonTooltip,
                ] : [
                    ...arenaTooltips,
                    buttonTooltip,
                    rewardValueTooltip,
                ]

                return [
                    ...initial,
                    ...tooltipsSet,
                ]
            }, [])
        }

        return {
            content: (
                <Battle
                    type={UNIT_TYPES.FORT_BATTLE}
                    battleRegulations={battleMode}
                    primetimeStart={msecondsToMatchMaker + Date.now()}
                    schedule={peripherySchedule}
                >
                    {buttons}
                </Battle>
            ),
            tooltips,
        }
    }

    render() {
        if (!this.props.loaded || this.props.loading) return (
            <div className={styles.base}>
                <LoadingScreen type='window' />
            </div>
        )

        const { content: sortiesContent, tooltips: sortiesTooltips } = this.getSortiesContent()
        const { content: fortBattlesContent, tooltips: fortBattleTooltips } = this.getFortBattlesContent()

        const staticTooltips = Object.keys(STATIC_TOOLTIPS_ID).map(item => (
            <WizardDefaultTooltip key={STATIC_TOOLTIPS_ID[item]} tooltipId={STATIC_TOOLTIPS_ID[item]}>
                <p>{STATIC_TOOLTIPS_MESSAGES[item]}</p>
            </WizardDefaultTooltip>
        ))

        return (
            <div className={styles.base}>
                {this.props.industrialResourceMultiplier > 1 &&
                    <div className={styles.multiplier} data-tip data-for={IndustrialResourceCoefTooltip.id}>
                        {INDUSTRIAL_RESOURCE_MULTIPLIER_TEXT(
                            <span className={styles.multiplierValue}>
                                {INDUSTRIAL_RESOURCE_MULTIPLIER(this.props.industrialResourceMultiplier)}
                            </span>,
                            true
                        )}
                    </div>
                }
                {this.props.industrialResourceMultiplier > 1 &&
                    <IndustrialResourceCoefTooltip multiplier={this.props.industrialResourceMultiplier} />
                }
                <div className={styles.holder}>
                    <div className={styles.part}>
                        <div className={styles.inner}>
                            {sortiesContent}
                        </div>
                    </div>
                    <div className={styles.part}>
                        <div className={styles.inner}>
                            {fortBattlesContent}
                        </div>
                    </div>
                </div>
                <BattleTitleTooltip
                    type={UNIT_TYPES.SORTIE}
                    peripheries={this.props.sorties.peripheries}
                    showSchedule={this.props.showSchedule}
                />
                <BattleTitleTooltip
                    type={UNIT_TYPES.FORT_BATTLE}
                    peripheries={this.props.fortBattles.peripheries}
                    showSchedule={this.props.showSchedule}
                />

                {sortiesTooltips}
                {fortBattleTooltips}
                {staticTooltips}
            </div>
        )
    }
}



// WEBPACK FOOTER //
// ./components/BattleWizard/BattleWizard.jsx